import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Modal, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileInvoice, faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../providers/authprovider";
import { usePrivilege } from "../../providers/privilegeprovider";
import handleResponseCode from "./utils/handleResponseCode";
import useInvoiceModal from "./hooks/useInvoiceModal";
import RecurringInvoiceModal from "./modals/RecurringInvoiceModal";
import {
  formatAmount,
  formatDate,
  frequencyLabel,
  nextInvoiceDate,
} from "./utils/recurringDates";

// najbliższy termin na górze; po wystawieniu data przesuwa się w przyszłość, więc pozycja spada na koniec
const byNextDate = (a, b) =>
  String(a.next_invoice_date || "").localeCompare(String(b.next_invoice_date || "")) ||
  String(a.building?.name || "").localeCompare(String(b.building?.name || ""), "pl");

export default function RecurringInvoices({ onInvoiceAdded }) {
  const { invoices: invoicesApi } = useAuth();
  const { getPrivilege } = usePrivilege();
  const privilege = getPrivilege("invoices");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // modal wystawienia faktury (wspólny z fakturami z wydarzeń)
  const { createInvoiceModal, CreateInvoiceModal } = useInvoiceModal();

  // modal dodaj / edytuj
  const [formOpen, setFormOpen] = useState(false);
  const [formTarget, setFormTarget] = useState(null);

  // modal potwierdzenia usunięcia
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError("");

    const result = await invoicesApi.fetchRecurringInvoices();

    if (!result?.success) {
      setItems([]);
      setError(handleResponseCode(result?.status, result?.error));
      setLoading(false);
      return;
    }

    setItems(result.content || []);
    setLoading(false);
  }, [invoicesApi]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const sortedItems = useMemo(() => [...items].sort(byNextDate), [items]);

  const openForm = (item = null) => {
    setError("");
    setInfo("");
    setFormTarget(item);
    setFormOpen(true);
  };

  const handleFormSaved = (saved) => {
    if (!saved?.id) {
      loadItems();
      return;
    }
    setItems((old) =>
      old.some((i) => i.id === saved.id)
        ? old.map((i) => (i.id === saved.id ? saved : i))
        : [...old, saved],
    );
    setInfo(formTarget ? "Zapisano zmiany faktury cyklicznej." : "Dodano fakturę cykliczną.");
  };

  const openIssue = (item) => {
    setError("");
    setInfo("");
    createInvoiceModal.show({
      title: "Wystaw fakturę cykliczną",
      initialBuilding: item.building,
      initialDescription: item.description || "",
      recurringInvoiceId: item.id,
      initialNextInvoiceDate: nextInvoiceDate(item.next_invoice_date, item.frequency),
      onSaved: (invoice, building, { nextInvoiceDate: next } = {}) => {
        setItems((old) =>
          old.map((i) =>
            i.id === item.id
              ? { ...i, next_invoice_date: next, last_issued_at: new Date().toISOString() }
              : i,
          ),
        );
        setInfo(`Wystawiono fakturę dla obiektu ${item.building?.name || ""}.`);
        onInvoiceAdded?.(invoice, building);
      },
    });
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;

    setDeleting(true);
    setError("");

    const result = await invoicesApi.deleteRecurringInvoice(deleteTarget.id);

    if (!result?.success) {
      setError(handleResponseCode(result?.status, result?.error));
    } else {
      setItems((old) => old.filter((i) => i.id !== deleteTarget.id));
      setInfo("Usunięto fakturę cykliczną.");
    }

    setDeleting(false);
    setDeleteTarget(null);
  };

  return (
    <div className="mt-5">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-1">
          Faktury cykliczne <Badge bg="secondary">{items.length}</Badge>
        </h4>
        {privilege.edit && (
          <Button onClick={() => openForm(null)}>Dodaj</Button>
        )}
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {info ? (
        <Alert variant="success" onClose={() => setInfo("")} dismissible>
          {info}
        </Alert>
      ) : null}

      {loading ? (
        <div className="py-3">
          <Spinner size="sm" className="me-2" />
          Ładowanie...
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>L.p.</th>
              <th>Obiekt</th>
              <th>Numer umowy</th>
              <th>Częstotliwość</th>
              <th>Kwota</th>
              <th>Data kolejnej faktury</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  Brak faktur cyklicznych.
                </td>
              </tr>
            ) : (
              sortedItems.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>{item.building?.name || "-"}</td>
                  <td>{item.contract_number || "-"}</td>
                  <td>{frequencyLabel(item.frequency)}</td>
                  <td className="text-nowrap">{formatAmount(item.amount)}</td>
                  <td>{formatDate(item.next_invoice_date)}</td>
                  <td className="text-nowrap">
                    {privilege.edit && (
                      <div className="d-flex gap-2">
                        <Button onClick={() => openIssue(item)} title="Wystaw">
                          <FontAwesomeIcon icon={faFileInvoice} />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={() => openForm(item)}
                          title="Edytuj"
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          onClick={() => setDeleteTarget(item)}
                          title="Usuń"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      <RecurringInvoiceModal
        show={formOpen}
        onHide={() => setFormOpen(false)}
        recurringInvoice={formTarget}
        onSaved={handleFormSaved}
      />

      {/* MODAL: potwierdzenie usunięcia */}
      <Modal show={!!deleteTarget} onHide={() => !deleting && setDeleteTarget(null)} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Potwierdź usunięcie</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">Czy na pewno chcesz usunąć fakturę cykliczną?</div>
          <div className="border rounded p-2">
            <div>
              <strong>Obiekt:</strong> {deleteTarget?.building?.name || "-"}
            </div>
            <div>
              <strong>Numer umowy:</strong> {deleteTarget?.contract_number || "-"}
            </div>
            <div>
              <strong>Częstotliwość:</strong> {frequencyLabel(deleteTarget?.frequency)}
            </div>
            <div>
              <strong>Kwota:</strong> {formatAmount(deleteTarget?.amount)}
            </div>
          </div>
          <div className="text-muted mt-2" style={{ fontSize: 13 }}>
            Wystawione już faktury pozostaną w folderze obiektu.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Anuluj
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Usuwanie...
              </>
            ) : (
              "Usuń"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <CreateInvoiceModal />
    </div>
  );
}
